import { ref } from 'vue';
import { useToast } from '@/composables/common/useToast';

/**
 * Composable genérico para descargar un documento a partir de un fetcher.
 *
 * Vive en `admin/documentos` porque el contrato (blob → ObjectURL → click) es
 * compartido por todos los contextos admin que tengan que servir archivos con
 * `Content-Disposition: attachment`. El fetcher concreto lo aporta cada dominio
 * (abonados, bases…) para no acoplar este composable a una ruta concreta.
 *
 * Si el fetcher devuelve `null`/`undefined`, se interpreta como "ya gestioné
 * la descarga por mi cuenta (ej. fallback cross-domain)" y se omite el flujo
 * de Blob del composable.
 *
 * @param {Function} fetcher Función que recibe `(documento, ...args)` y devuelve
 *   una Promise<AxiosResponse> con `responseType: 'blob'`, o `null`/`undefined`
 *   si ya gestionó la descarga nativamente.
 * @returns {{ download: Function, loading: Ref<boolean> }}
 */
export function useDocumentDownload(fetcher) {
    const toast = useToast();
    const loading = ref(false);

    async function download(documento, ...fetcherArgs) {
        // Single-flight guard: si ya hay una descarga en curso, ignorar
        // llamadas concurrentes. Evita race conditions cuando el usuario hace
        // doble-click o varios botones disparan el mismo handler a la vez.
        if (loading.value) return;

        if (!documento || !documento.id) {
            toast.error('Documento inválido.');
            return;
        }

        // Note: we deliberately do NOT require `documento.url` here. The
        // fetcher constructs the endpoint from `documento.id` (see
        // useBaseDocumentDownload / useAbonadoDocumentDownload), so the
        // download works regardless of whether the resource includes a
        // `url` field for image previews. Only `doc.nombre` and `doc.id`
        // are required downstream.

        loading.value = true;
        try {
            const response = await fetcher(documento, ...fetcherArgs);

            // Si el fetcher devuelve null/undefined, significa que ya gestionó
            // la descarga por su cuenta (ej. fallback cross-domain con <a>).
            // Saltamos el flujo de Blob del composable.
            if (!response) return;

            // 1) Sacamos el filename del Content-Disposition (lo pone el backend).
            // 2) Sacamos el Content-Type de la response. Vamos a necesitarlo para
            //    crear el Blob con el MIME correcto: si el Blob se crea sin tipo,
            //    Windows / macOS lo tratan como texto plano y al guardarlo junto
            //    con un nombre sin extensión lo renombran a `.txt` aunque el
            //    contenido sea un PDF válido.
            const disposition = response.headers?.['content-disposition']
                || response.headers?.['Content-Disposition']
                || '';
            const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
            const filename = match
                ? decodeURIComponent(match[1])
                : (documento.nombre || 'documento');

            const mime = (response.headers?.['content-type']
                || response.headers?.['Content-Type']
                || 'application/octet-stream')
                .split(';')[0]
                .trim()
                .toLowerCase();

            // Si el filename no trae una extensión de 2-5 chars, se la pegamos
            // desde el MIME. Caso real: el admin sube un PDF llamado "DNI frente"
            // (sin .pdf) y al descargar el archivo quedaba sin extensión.
            const EXT_BY_MIME = {
                'application/pdf': 'pdf',
                'image/jpeg': 'jpg',
                'image/png': 'png',
                'image/gif': 'gif',
                'image/bmp': 'bmp',
                'image/tiff': 'tif',
            };
            const extFromMime = EXT_BY_MIME[mime] || '';
            const hasExt = /\.[a-z0-9]{2,5}$/i.test(filename);
            const safeFilename = hasExt
                ? filename
                : (filename + (extFromMime ? '.' + extFromMime : ''));

            const blob = new Blob([response.data], { type: mime });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = safeFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            toast.error('No se pudo descargar el documento.');
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useDocumentDownload] error:', error);
            }
        } finally {
            loading.value = false;
        }
    }

    return { download, loading };
}